import { inject, Injectable } from '@angular/core';

import { NotificationSnackbarPrimitive } from '../../ui/primitives/notification-snackbar/notification-snackbar.primitive';

/**
 * Single entry point for user-visible notifications.
 *
 * The error HTTP interceptor uses this service for unhandled 5xx + network
 * failures.  Feature code uses it for confirmations after writes, etc.
 *
 * Delegates rendering to `NotificationSnackbarPrimitive` (which wraps
 * `@angular/material/snack-bar`). Keeping the Material dependency
 * contained to the primitive layer means we can swap toast libraries
 * later without touching feature code or this service.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly snackbar = inject(NotificationSnackbarPrimitive);

  success(message: string): void {
    this.snackbar.success(message);
  }

  info(message: string): void {
    this.snackbar.info(message);
  }

  warning(message: string): void {
    this.snackbar.warning(message);
  }

  error(message: string, action = 'Dismiss'): void {
    this.snackbar.error(message, { action });
  }

  /**
   * Silent-refresh success toast (Phase H Stage 1 / HQ2). Positioned at
   * top-inline-end — `horizontalPosition: 'end'` is RTL-aware: right in
   * LTR, left in RTL. Auto-dismiss after the success duration.
   */
  sessionRefreshed(): void {
    this.snackbar.success('Session refreshed.', {
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }
}
